import React, { useEffect, useState } from "react";
import { X, Save, Building2 } from "lucide-react";
import { getSettings, saveSettings } from "../api";

const FIELDS = [
  { key: "company_name", label: "Company Name", ph: "e.g. Acme Heating & Air" },
  { key: "company_address", label: "Company Address", ph: "Street, City, ST ZIP" },
  { key: "shared_email", label: "Customer Email (used for every customer)", ph: "registrations@yourcompany.com" },
  { key: "shared_phone", label: "Customer Phone (used for every customer)", ph: "(555) 123-4567" },
  { key: "installer_name", label: "Installer / Contractor Name", ph: "Your company or installer name" },
  { key: "installer_email", label: "Installer Email", ph: "installer@yourcompany.com" },
  { key: "installer_phone", label: "Installer Phone", ph: "(555) 123-4567" },
  { key: "daikin_dealer_name", label: "Daikin Dealer Name (as listed on Daikin's contractor search)", ph: "e.g. A#1 AIR, INC" },
];

export default function SettingsModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) getSettings().then(setForm);
  }, [open]);

  if (!open || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveSettings(form);
      onSaved && onSaved(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="settings-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-slate-700 bg-[#161f32] p-6 shadow-2xl fade-up">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-slate-100">
            <Building2 className="h-5 w-5 text-orange-400" /> Company / Vendor Details
          </h2>
          <button data-testid="settings-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-400">These details are applied to every Trane registration (owner contact + installer info). Enter your own company's information.</p>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-medium text-slate-400">{f.label}</label>
              <input
                data-testid={`settings-input-${f.key}`}
                value={form[f.key] || ""}
                placeholder={f.ph}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-orange-500"
              />
            </div>
          ))}
        </div>
        <button
          data-testid="settings-save-button"
          onClick={save}
          disabled={saving}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 py-2.5 font-semibold text-white hover:bg-orange-500 disabled:opacity-60"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Defaults"}
        </button>
      </div>
    </div>
  );
}
